import { Instagram, Music2, Linkedin, Star, Crown } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';

interface UpgradePromoProps {
  userName?: string;
  isPreview?: boolean;
}

const UpgradePromo = ({
  userName = 'Voyager',
  isPreview = false
}: UpgradePromoProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleUpgrade = async (tier: 'premium_economy' | 'first_class') => {
    if (!isPreview) return;

    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to upgrade your subscription.",
        variant: "destructive",
      });
      navigate('/auth');
      return;
    }

    try {
      toast({
        title: "Redirecting to checkout...",
        description: "Please wait while we prepare your payment.",
      });

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          tier,
          return_url: window.location.origin + '/pricing'
        }
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        title: "Checkout Error",
        description: "Failed to initiate checkout. Please try again.",
        variant: "destructive",
      });
    }
  };
  return <>
    <style>{`
      @media (max-width: 640px) {
        .email-container { padding: 24px 12px !important; }
        .email-logo { width: 180px !important; }
        .email-heading { font-size: 24px !important; }
        .email-text { font-size: 14px !important; line-height: 22px !important; }
        .email-tier-box { padding: 24px 16px !important; margin: 24px 0 !important; }
        .email-tier-icon { padding: 10px !important; }
        .email-tier-title { font-size: 22px !important; }
        .email-tier-price { font-size: 18px !important; }
        .email-features-box { padding: 16px !important; }
        .email-feature-item { font-size: 13px !important; line-height: 24px !important; padding-left: 24px !important; }
        .email-button { padding: 12px 20px !important; font-size: 14px !important; }
        .email-offer-badge { padding: 6px 16px !important; font-size: 10px !important; }
        .email-social-icon { width: 24px !important; height: 24px !important; }
      }
    `}</style>
    <div style={{
    backgroundColor: '#f0fafa',
    minHeight: '100vh',
    padding: '40px 20px',
    fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif"
  }}>
      <div className="email-container" style={{
      backgroundColor: '#ffffff',
      margin: '0 auto',
      padding: '40px 20px',
      borderRadius: '8px',
      maxWidth: '600px'
    }}>
        <div style={{
        textAlign: 'center',
        marginBottom: '0'
      }}>
          <img src="/lovable-uploads/odyvoy-logo.png" alt="OdyVoy Logo" className="email-logo" style={{
          width: '250px',
          height: 'auto',
          margin: '0 auto'
        }} />
        </div>
        <h1 className="email-heading" style={{
        color: '#1a9a9a',
        fontSize: '32px',
        fontWeight: 'bold',
        margin: '8px 0 24px',
        padding: '0',
        textAlign: 'center'
      }}>
          Elevate Your Travel Experience ✈️
        </h1>
        
        <p className="email-text" style={{
        color: '#333',
        fontSize: '16px',
        lineHeight: '24px',
        margin: '16px 0'
      }}>
          Hi {userName},
        </p>
        
        <p className="email-text" style={{
        color: '#333',
        fontSize: '16px',
        lineHeight: '24px',
        margin: '16px 0'
      }}>
          You've been making great use of OdyVoy's free features! We noticed you're planning some exciting trips, and we wanted to show you what you're missing with our premium subscriptions.
        </p>
        
        <div className="email-tier-box" style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '32px 24px',
        margin: '32px 0',
        border: '3px solid #1a9a9a',
        boxShadow: '0 4px 12px rgba(26, 154, 154, 0.1)'
      }}>
          <div style={{
          textAlign: 'center',
          marginBottom: '20px'
        }}>
            <div style={{
              display: 'inline-block',
              padding: '14px',
              borderRadius: '16px',
              backgroundColor: '#1a9a9a',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }} className="email-tier-icon">
              <Star size={28} color="#ffffff" strokeWidth={2} />
            </div>
          </div>
          <div className="email-offer-badge" style={{
          backgroundColor: 'transparent',
          backgroundImage: 'linear-gradient(to right, #1a9a9a, #6dd4d4)',
          padding: '8px 20px',
          borderRadius: '6px',
          display: 'inline-block',
          margin: '0 auto 16px',
          width: 'fit-content',
          position: 'relative',
          left: '50%',
          transform: 'translateX(-50%)',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        }}>
            <p style={{
            color: '#ffffff',
            fontSize: '12px',
            fontWeight: '700',
            textAlign: 'center',
            margin: '0',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>✨ Most Popular</p>
          </div>
          <h2 className="email-tier-title" style={{
          color: '#1a9a9a',
          fontSize: '28px',
          fontWeight: '700',
          margin: '0 0 8px',
          padding: '0',
          textAlign: 'center',
          letterSpacing: '-0.5px'
        }}>Premium Economy Class Pack</h2>
          <p className="email-tier-price" style={{
          color: '#1a9a9a',
          fontSize: '20px',
          fontWeight: '600',
          textAlign: 'center',
          margin: '0 0 24px'
        }}>
            €6.99/month or €69.90/year
          </p>
          
          <div className="email-features-box" style={{
          backgroundColor: '#f8fafa',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '24px'
        }}>
            <ul style={{
            margin: '0',
            padding: '0',
            listStyle: 'none'
          }}>
              <li className="email-feature-item" style={{
              color: '#333',
              fontSize: '15px',
              lineHeight: '26px',
              margin: '12px 0',
              paddingLeft: '28px',
              position: 'relative'
            }}>
                <span style={{
                position: 'absolute',
                left: '0',
                color: '#1a9a9a',
                fontWeight: '600'
              }}>✨</span> Unlimited Searches To All Features
              </li>
              <li className="email-feature-item" style={{
              color: '#333',
              fontSize: '15px',
              lineHeight: '26px',
              margin: '12px 0',
              paddingLeft: '28px',
              position: 'relative'
            }}>
                <span style={{
                position: 'absolute',
                left: '0',
                color: '#1a9a9a',
                fontWeight: '600'
              }}>🤖</span> AI-Powered Personalized Lists
              </li>
              <li className="email-feature-item" style={{
              color: '#333',
              fontSize: '15px',
              lineHeight: '26px',
              margin: '12px 0',
              paddingLeft: '28px',
              position: 'relative'
            }}>
                <span style={{
                position: 'absolute',
                left: '0',
                color: '#1a9a9a',
                fontWeight: '600'
              }}>🛂</span> Visa Requirements Check
              </li>
              <li className="email-feature-item" style={{
              color: '#333',
              fontSize: '15px',
              lineHeight: '26px',
              margin: '12px 0',
              paddingLeft: '28px',
              position: 'relative'
            }}>
                <span style={{
                position: 'absolute',
                left: '0',
                color: '#1a9a9a',
                fontWeight: '600'
              }}>🗺️</span> Local Destination Tips
              </li>
              <li className="email-feature-item" style={{
              color: '#333',
              fontSize: '15px',
              lineHeight: '26px',
              margin: '12px 0',
              paddingLeft: '28px',
              position: 'relative'
            }}>
                <span style={{
                position: 'absolute',
                left: '0',
                color: '#1a9a9a',
                fontWeight: '600'
              }}>💪</span> Health & Wellness Preparation
              </li>
              <li className="email-feature-item" style={{
              color: '#333',
              fontSize: '15px',
              lineHeight: '26px',
              margin: '12px 0',
              paddingLeft: '28px',
              position: 'relative'
            }}>
                <span style={{
                position: 'absolute',
                left: '0',
                color: '#1a9a9a',
                fontWeight: '600'
              }}>✈️</span> Jet Lag Minimizer
              </li>
              <li className="email-feature-item" style={{
              color: '#333',
              fontSize: '15px',
              lineHeight: '26px',
              margin: '12px 0',
              paddingLeft: '28px',
              position: 'relative'
            }}>
                <span style={{
                position: 'absolute',
                left: '0',
                color: '#1a9a9a',
                fontWeight: '600'
              }}>💱</span> Smart Currency Converter
              </li>
              <li className="email-feature-item" style={{
              color: '#333',
              fontSize: '15px',
              lineHeight: '26px',
              margin: '12px 0',
              paddingLeft: '28px',
              position: 'relative'
            }}>
                <span style={{
                position: 'absolute',
                left: '0',
                color: '#1a9a9a',
                fontWeight: '600'
              }}>💰</span> Group Expense Sharing
              </li>
              <li className="email-feature-item" style={{
              color: '#333',
              fontSize: '15px',
              lineHeight: '26px',
              margin: '12px 0',
              paddingLeft: '28px',
              position: 'relative'
            }}>
                <span style={{
                position: 'absolute',
                left: '0',
                color: '#1a9a9a',
                fontWeight: '600'
              }}>🎁</span> Early Access To New Features
              </li>
            </ul>
          </div>

          {isPreview ? (
            <button
              onClick={() => handleUpgrade('premium_economy')}
              className="email-button"
              style={{
                backgroundColor: '#1a9a9a',
                borderRadius: '8px',
                color: '#ffffff',
                fontSize: '16px',
                fontWeight: '700',
                textDecoration: 'none',
                textAlign: 'center',
                display: 'block',
                padding: '16px 24px',
                margin: '0',
                boxShadow: '0 2px 8px rgba(26, 154, 154, 0.2)',
                border: 'none',
                width: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#158383';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(26, 154, 154, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#1a9a9a';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(26, 154, 154, 0.2)';
              }}
            >
              Upgrade to Premium Economy Pack →
            </button>
          ) : (
            <a href="/pricing" className="email-button" style={{
              backgroundColor: '#1a9a9a',
              borderRadius: '8px',
              color: '#ffffff',
              fontSize: '16px',
              fontWeight: '700',
              textDecoration: 'none',
              textAlign: 'center',
              display: 'block',
              padding: '16px 24px',
              margin: '0',
              boxShadow: '0 2px 8px rgba(26, 154, 154, 0.2)'
            }}>
              Upgrade to Premium Economy Pack
            </a>
          )}
        </div>

        <div className="email-tier-box" style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '32px 24px',
        margin: '32px 0',
        border: '3px solid #f0d763',
        boxShadow: '0 4px 12px rgba(240, 215, 99, 0.15)'
      }}>
          <div style={{
          textAlign: 'center',
          marginBottom: '20px'
        }}>
            <div style={{
              display: 'inline-block',
              padding: '14px',
              borderRadius: '16px',
              backgroundColor: '#f0d763',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }} className="email-tier-icon">
              <Crown size={28} color="#ffffff" strokeWidth={2} />
            </div>
          </div>
          <h2 className="email-tier-title" style={{
          color: '#f0d763',
          fontSize: '28px',
          fontWeight: '700',
          margin: '0 0 8px',
          padding: '0',
          textAlign: 'center',
          letterSpacing: '-0.5px'
        }}>
            First Class Pack
          </h2>
          <p className="email-tier-price" style={{
          color: '#f0d763',
          fontSize: '20px',
          fontWeight: '600',
          textAlign: 'center',
          margin: '0 0 24px'
        }}>
            €11.99/month or €119.90/year
          </p>
          
          <div className="email-features-box" style={{
          backgroundColor: '#fffef8',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '24px',
          border: '1px solid #f0d763'
        }}>
            <ul style={{
            margin: '0',
            padding: '0',
            listStyle: 'none'
          }}>
              <li className="email-feature-item" style={{
              color: '#333',
              fontSize: '15px',
              lineHeight: '26px',
              margin: '12px 0',
              paddingLeft: '28px',
              position: 'relative'
            }}>
                <span style={{
                position: 'absolute',
                left: '0',
                color: '#f0d763',
                fontWeight: '600'
              }}>✨</span> Everything In Premium Economy
              </li>
              <li className="email-feature-item" style={{
              color: '#333',
              fontSize: '15px',
              lineHeight: '26px',
              margin: '12px 0',
              paddingLeft: '28px',
              position: 'relative'
            }}>
                <span style={{
                position: 'absolute',
                left: '0',
                color: '#f0d763',
                fontWeight: '600'
              }}>📱</span> Offline Mode - Access Lists Without Internet
              </li>
              <li className="email-feature-item" style={{
              color: '#333',
              fontSize: '15px',
              lineHeight: '26px',
              margin: '12px 0',
              paddingLeft: '28px',
              position: 'relative'
            }}>
                <span style={{
                position: 'absolute',
                left: '0',
                color: '#f0d763',
                fontWeight: '600'
              }}>🗺️</span> Multi-City Packing Lists
              </li>
              <li className="email-feature-item" style={{
              color: '#333',
              fontSize: '15px',
              lineHeight: '26px',
              margin: '12px 0',
              paddingLeft: '28px',
              position: 'relative'
            }}>
                <span style={{
                position: 'absolute',
                left: '0',
                color: '#f0d763',
                fontWeight: '600'
              }}>📋</span> Custom Travel Templates - Save & Reuse Lists
              </li>
              <li className="email-feature-item" style={{
              color: '#333',
              fontSize: '15px',
              lineHeight: '26px',
              margin: '12px 0',
              paddingLeft: '28px',
              position: 'relative'
            }}>
                <span style={{
                position: 'absolute',
                left: '0',
                color: '#f0d763',
                fontWeight: '600'
              }}>📊</span> Advanced Travel Analytics - Spending & Travel Insights
              </li>
              <li className="email-feature-item" style={{
              color: '#333',
              fontSize: '15px',
              lineHeight: '26px',
              margin: '12px 0',
              paddingLeft: '28px',
              position: 'relative'
            }}>
                <span style={{
                position: 'absolute',
                left: '0',
                color: '#f0d763',
                fontWeight: '600'
              }}>👥</span> Group Packing Progress Sharing
              </li>
            </ul>
          </div>

          {isPreview ? (
            <button
              onClick={() => handleUpgrade('first_class')}
              className="email-button"
              style={{
                backgroundColor: '#f0d763',
                borderRadius: '8px',
                color: '#1a9a9a',
                fontSize: '16px',
                fontWeight: '700',
                textDecoration: 'none',
                textAlign: 'center',
                display: 'block',
                padding: '16px 24px',
                margin: '0',
                boxShadow: '0 2px 8px rgba(240, 215, 99, 0.3)',
                border: 'none',
                width: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e8cf5a';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(240, 215, 99, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f0d763';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(240, 215, 99, 0.3)';
              }}
            >
              Upgrade to First Class Pack →
            </button>
          ) : (
            <a href="/pricing" className="email-button" style={{
              backgroundColor: '#f0d763',
              borderRadius: '8px',
              color: '#1a9a9a',
              fontSize: '16px',
              fontWeight: '700',
              textDecoration: 'none',
              textAlign: 'center',
              display: 'block',
              padding: '16px 24px',
              margin: '0',
              boxShadow: '0 2px 8px rgba(240, 215, 99, 0.3)'
            }}>
              Upgrade to First Class Pack
            </a>
          )}
        </div>

        <div style={{
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        padding: '20px',
        margin: '24px 0',
        textAlign: 'center'
      }}>
          <p style={{
          color: '#1a9a9a',
          fontSize: '18px',
          fontWeight: 'bold',
          margin: '0 0 8px'
        }}>
            🎉 Special Offer: Get 2 Months Free
          </p>
          <p style={{
          color: '#333',
          fontSize: '14px',
          lineHeight: '20px',
          margin: '0'
        }}>
            When you upgrade to any annual plan subscription
          </p>
        </div>

        <hr style={{
        borderColor: '#e6e6e6',
        margin: '32px 0'
      }} />

        <div style={{
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        padding: '24px',
        margin: '24px 0',
        textAlign: 'center'
      }}>
          <p style={{
          color: '#333',
          fontSize: '18px',
          fontWeight: 'bold',
          margin: '0 0 12px'
        }}>Still Not Sure? Try a Boost Pass First! ⚡</p>
          <p style={{
          color: '#333',
          fontSize: '15px',
          lineHeight: '22px',
          margin: '0 0 20px'
        }}>
            Experience premium features before committing to a subscription with our flexible boost passes starting at €3.49.
          </p>
          <a href="/pricing" style={{
          backgroundColor: '#1a9a9a',
          borderRadius: '6px',
          color: '#ffffff',
          fontSize: '16px',
          fontWeight: 'bold',
          textDecoration: 'none',
          textAlign: 'center',
          display: 'inline-block',
          padding: '12px 32px',
          margin: '0'
        }}>
            View Boost Passes
          </a>
        </div>

        <p style={{
        color: '#333',
        fontSize: '16px',
        lineHeight: '24px',
        marginTop: '32px'
      }}>
          Safe travels,
          <br />
          The OdyVoy Team 🌟
        </p>

        <hr style={{
        borderColor: '#e6e6e6',
        margin: '32px 0'
      }} />

        <div style={{
        textAlign: 'center',
        margin: '32px 0'
      }}>
          <p style={{
          color: '#333',
          fontSize: '16px',
          fontWeight: 'bold',
          marginBottom: '16px'
        }}>
            Stay Connected With Us
          </p>
          <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '20px',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
            <a href="https://www.instagram.com/odyvoy_/" style={{
            textDecoration: 'none',
            color: '#64748b',
            transition: 'color 0.2s'
          }} aria-label="Follow us on Instagram">
              <Instagram className="email-social-icon" size={28} strokeWidth={1.5} />
            </a>
            <a href="https://www.tiktok.com/@odyvoy_" style={{
            textDecoration: 'none',
            color: '#64748b',
            transition: 'color 0.2s'
          }} aria-label="Follow us on TikTok">
              <Music2 className="email-social-icon" size={28} strokeWidth={1.5} />
            </a>
            <a href="https://www.linkedin.com/company/odyvoy/about/?viewAsMember=true" style={{
            textDecoration: 'none',
            color: '#64748b',
            transition: 'color 0.2s'
          }} aria-label="Follow us on LinkedIn">
              <Linkedin className="email-social-icon" size={28} strokeWidth={1.5} />
            </a>
          </div>
          
        </div>

        <div style={{
        textAlign: 'center',
        marginTop: '24px',
        paddingTop: '24px',
        borderTop: '1px solid #e6e6e6'
      }}>
          <p style={{
          color: '#8898aa',
          fontSize: '12px',
          lineHeight: '18px',
          margin: '8px 0'
        }}>
            <strong>OdyVoy</strong>
          </p>
          <p style={{
          color: '#8898aa',
          fontSize: '12px',
          lineHeight: '18px',
          margin: '8px 0'
        }}>Your AI-Powered Travel Companion</p>
          <p style={{
          color: '#8898aa',
          fontSize: '12px',
          lineHeight: '18px',
          margin: '16px 0'
        }}>
            <a href="/unsubscribe" style={{
            color: '#1a9a9a',
            textDecoration: 'underline'
          }}>Unsubscribe</a>
            {' | '}
            <a href="/privacy" style={{
            color: '#1a9a9a',
            textDecoration: 'underline'
          }}>Privacy Policy</a>
            {' | '}
            <a href="/terms" style={{
            color: '#1a9a9a',
            textDecoration: 'underline'
          }}>Terms of Service</a>
          </p>
          <p style={{
          color: '#8898aa',
          fontSize: '11px',
          lineHeight: '16px',
          margin: '16px 0'
        }}>
            © {new Date().getFullYear()} OdyVoy. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  </>;
};
export default UpgradePromo;